import { useStyleRegister } from '@ant-design/cssinjs';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Empty, Flex, Spin, theme, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';

const createVideoCoverStyles = (token) => ({
  '.kip-video-cover': {
    alignItems: 'center',
    aspectRatio: '16 / 9',
    background: token.colorBgLayout,
    display: 'flex',
    justifyContent: 'center',
    marginInline: 'auto',
    maxWidth: 1024,
    overflow: 'hidden',
    position: 'relative',
    width: 1024,
  },
  '.kip-video-cover-video': {
    background: token.colorBgLayout,
    height: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
    width: 1024,
  },
  '.kip-video-cover-overlay': {
    alignItems: 'center',
    background: token.colorBgMask,
    display: 'flex',
    inset: 0,
    justifyContent: 'center',
    position: 'absolute',
  },
  '.kip-video-cover-error-text': {
    color: token.colorTextLightSolid,
  },
});

const VideoCover = ({ autoPlay, onRetry, retrying, reloadKey, title, src }) => {
  const { csp } = useContext(ConfigProvider.ConfigContext);
  const { hashId, theme: tokenTheme, token } = theme.useToken();
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(Boolean(autoPlay && src));
  const [playbackFailed, setPlaybackFailed] = useState(false);

  useStyleRegister(
    {
      hashId,
      layer: {
        name: 'antd',
      },
      nonce: () => csp?.nonce,
      path: ['VideoCover'],
      theme: tokenTheme,
      token,
    },
    () => createVideoCoverStyles(token),
  );

  useEffect(() => {
    setPlaybackFailed(false);
    setLoading(Boolean(autoPlay && src));
    if (!autoPlay || !src || !videoRef.current) {
      return;
    }

    const playResult = videoRef.current.play?.();
    if (playResult?.catch) {
      playResult.catch(() => {
        setLoading(false);
        setPlaybackFailed(true);
      });
    }
  }, [autoPlay, reloadKey, src]);

  if (!src) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无视频" />;
  }

  return (
    <div className={`kip-video-cover ${hashId}`}>
      {playbackFailed ? null : (
        /* eslint-disable-next-line jsx-a11y/media-has-caption */
        <video
          ref={videoRef}
          aria-label={title || '视频'}
          autoPlay={autoPlay}
          className={`kip-video-cover-video ${hashId}`}
          controls
          playsInline={autoPlay}
          preload={autoPlay ? 'auto' : 'metadata'}
          src={src}
          width={1024}
          onCanPlay={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setPlaybackFailed(true);
          }}
          onPlaying={() => setLoading(false)}
        />
      )}
      {loading && !playbackFailed ? (
        <div className={`kip-video-cover-overlay ${hashId}`}>
          <Spin description="视频加载中" />
        </div>
      ) : null}
      {playbackFailed ? (
        <div className={`kip-video-cover-overlay ${hashId}`}>
          <Flex align="center" gap="small" vertical>
            <Typography.Text className={`kip-video-cover-error-text ${hashId}`}>
              视频加载失败
            </Typography.Text>
            {onRetry ? (
              <Button
                aria-label="重新加载"
                icon={<ReloadOutlined />}
                loading={retrying}
                shape="circle"
                size="large"
                type="primary"
                onClick={onRetry}
              />
            ) : null}
          </Flex>
        </div>
      ) : null}
    </div>
  );
};

VideoCover.propTypes = {
  autoPlay: PropTypes.bool,
  onRetry: PropTypes.func,
  reloadKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  retrying: PropTypes.bool,
  src: PropTypes.string,
  title: PropTypes.string,
};

VideoCover.defaultProps = {
  autoPlay: false,
  onRetry: undefined,
  reloadKey: undefined,
  retrying: false,
  src: '',
  title: '',
};

export default VideoCover;
